import { useState, useEffect } from "react";
import { publicApi } from "@/lib/api";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertTriangle, Info, CheckCircle2 } from "lucide-react";

// Pop-up BLOQUANT d'annonce dans l'espace (collaborateur / responsable).
// Récupère les annonces actives non acquittées, les présente une à une, et n'autorise
// la fermeture qu'après accusé de lecture explicite (case à cocher + bouton).
export function AnnouncementModal({ token, scope = "public" }) {
  const base = scope === "resp" ? `/pointage/resp/${token}` : `/pointage/public/${token}`;
  const [queue, setQueue] = useState([]);
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) return;
    let alive = true;
    publicApi.get(`${base}/announcements`)
      .then((r) => { if (alive) setQueue(Array.isArray(r.data) ? r.data : []); })
      .catch(() => { if (alive) setQueue([]); });
    return () => { alive = false; };
  }, [token, base]);

  const current = queue[0];
  if (!current) return null;

  const important = current.type === "important";
  const total = queue.length;

  const ack = async () => {
    if (!confirmed || busy) return;
    setBusy(true);
    try { await publicApi.post(`${base}/announcements/${current.id}/ack`); }
    catch { /* on retire localement même si le réseau échoue */ }
    setBusy(false);
    setConfirmed(false);
    setQueue((q) => q.slice(1));
  };

  return (
    <Dialog open={true}>
      <DialogContent
        className="max-w-md gap-0 overflow-hidden rounded-2xl border-0 p-0 [&>button]:hidden"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        data-testid="announcement-modal"
      >
        {/* Bandeau brandé SHARITI */}
        <div className="flex items-center gap-2 bg-[var(--shariti-orange)] px-4 py-3 text-white">
          <div className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-white/20">
            {important ? <AlertTriangle className="h-4 w-4" /> : <Info className="h-4 w-4" />}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] font-bold uppercase tracking-widest opacity-90">SHARITI · Annonce</div>
            <div className="truncate text-xs font-semibold">
              {current.association_acronym || current.association_name || "Message de la Direction"}
            </div>
          </div>
          <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold ${important ? "bg-white text-[#9A6207]" : "bg-white/20 text-white"}`}>
            {important ? "⚠️ Important" : "ℹ️ Info"}
          </span>
        </div>

        <div className="px-4 py-4">
          {total > 1 && (
            <div className="mb-2 text-[10px] font-bold uppercase tracking-wide text-muted-ink" data-testid="announcement-queue-count">
              Message 1 sur {total}
            </div>
          )}
          <h2 className="text-base font-extrabold leading-snug text-ink" data-testid="announcement-title">
            {current.title}
          </h2>

          <div className="mt-3 max-h-[45vh] overflow-y-auto whitespace-pre-wrap break-words text-[13px] leading-relaxed text-ink" data-testid="announcement-body">
            {current.body}
          </div>

          {current.signature ? (
            <div className="mt-4 whitespace-pre-wrap text-right text-[12px] font-semibold italic text-muted-ink" data-testid="announcement-signature">
              {current.signature}
            </div>
          ) : null}
        </div>

        {/* Accusé de lecture obligatoire */}
        <div className="border-t border-[var(--border-soft)] bg-[var(--surface-2)] px-4 py-3">
          <label className="flex cursor-pointer items-start gap-2 text-[12px] font-medium text-ink" data-testid="announcement-confirm-label">
            <Checkbox checked={confirmed} onCheckedChange={(v) => setConfirmed(!!v)} className="mt-0.5" data-testid="announcement-confirm-checkbox" />
            <span>Je confirme avoir <strong>lu et compris</strong> ce message.</span>
          </label>
          <Button
            onClick={ack}
            disabled={!confirmed || busy}
            className="mt-3 w-full bg-[var(--shariti-orange)] font-bold text-white hover:bg-[#E56622] disabled:opacity-50"
            data-testid="announcement-ack-btn"
          >
            <CheckCircle2 className="mr-1.5 h-4 w-4" />
            {busy ? "Enregistrement…" : "J'ai bien lu et compris"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default AnnouncementModal;
